/**
 * Name as per ID card and name as per land record (owner, 2026-09-21: "technically land record
 * and id card name shall match"). Compared as people.fn_name_key does (migration 0076): case,
 * spaces and punctuation ignored, so "A. K. Das" matches "a k das".
 */
export function nameKey(name?: string | null): string {
  // Latin, digits, and the Indic block from Devanagari through Sinhala - matras, nuktas and
  // viramas included, because they are part of the name, not punctuation. Written as codepoints:
  // the literal endpoints are a combining mark and a Sinhala letter, invisible in most editors.
  // eslint-disable-next-line no-misleading-character-class -- the range is deliberate; see above
  return (name ?? '').toLowerCase().replace(/[^a-z0-9\u0900-\u0DFF]+/g, '');
}

export function namesDiffer(recorded?: string | null, reference?: string | null): boolean {
  const a = nameKey(recorded);
  return a !== '' && a !== nameKey(reference);
}

/**
 * What confirms two differently written names are the same person - the CHECK values - each with
 * an example of when to choose it (owner, 2026-09-27: "give me examples, add i button ... for each?").
 */
export const NAME_MATCH_BASES = [
  {
    id: 'father_name_confirmed', label: "Father's / spouse's name confirmed on both",
    example: "Khatian: 'Sheik Rahim, s/o Abdul Karim'. Aadhaar: 'Rahim Sheik', father Abdul Karim. The same father on both.",
  },
  {
    id: 'two_ids_match', label: 'Two ID cards show the same name',
    example: "Aadhaar: 'Mohammed Sheik'. Land record: 'Md Sheik'. The voter ID also reads 'Md Sheik', so a second ID joins the two.",
  },
  {
    id: 'spelling_variation', label: 'Spelling variation of the same name',
    example: "'Lakshmi' and 'Laxmi', 'Chowdhury' and 'Choudhary', 'Mohammad' and 'Mohammed'.",
  },
  {
    id: 'initials_short_form', label: 'Initials or short form of the same name',
    example: "'Md Sheik' for 'Mohammed Sheik', 'K. Gowda' for 'Kaveri Gowda', 'Smt.' or 'Sri' left off.",
  },
  {
    id: 'inherited', label: "Land record still in a parent's name (inheritance)",
    example: "Khatian still reads the late father 'Ramesh Mandal'; the farmer is his son 'Suresh Mandal' and the mutation is not done.",
  },
  {
    id: 'co_sharer', label: 'Recorded holder is a co-sharer',
    example: "Joint family land recorded as 'Anil Das & others', or in the elder brother's name; the farmer holds a share.",
  },
  {
    id: 'other', label: 'Other (say in remarks)',
    example: "Anything else: a married name ('Rina Das', now 'Rina Saha'), a name changed by gazette notice. Explain in remarks.",
  },
];

/** The example for a basis, shown under the choice once it is made. */
export function nameMatchExample(id?: string | null): string | undefined {
  return NAME_MATCH_BASES.find((entry) => entry.id === id)?.example;
}

export function nameMatchLabel(id?: string | null): string {
  return NAME_MATCH_BASES.find((entry) => entry.id === id)?.label ?? '--';
}

/** Titles that come and go between records without changing who the person is. */
const HONORIFICS = new Set(['smt', 'sri', 'shri', 'shree', 'sh', 'mr', 'mrs', 'ms', 'kumari', 'kum', 'late', 'lt', 'dr', 'sk']);
/** Short forms written for a whole name. */
const SHORT_FORMS: Record<string, string[]> = {
  md: ['mohammed', 'mohammad', 'muhammad', 'mohamed', 'mahammad'],
  mohd: ['mohammed', 'mohammad', 'muhammad', 'mohamed'],
};

function tokens(name: string): string[] {
  // eslint-disable-next-line no-misleading-character-class -- the same Indic range as nameKey; see there
  return name.toLowerCase().replace(/[^a-z\u0900-\u0DFF]+/g, ' ').trim().split(/\s+/).filter((token) => token && !HONORIFICS.has(token));
}

/**
 * How a name sounds, for spellings that differ in Roman letters but not aloud: Laxmi / Lakshmi,
 * Choudhary / Chowdhury, Sheikh / Sheik, Pooja / Puja.
 */
function sound(word: string): string {
  return word
    .replace(/ksh/g, 'x').replace(/(kh|gh|ch|jh|th|dh|ph|bh|sh)/g, (pair) => pair[0]!)
    .replace(/(ee|ii)/g, 'i').replace(/(oo|uu|ou|ow)/g, 'u').replace(/aa/g, 'a').replace(/w/g, 'v')
    .replace(/(.)\1+/g, '$1').replace(/h$/, '').replace(/y$/, 'i');
}

function editDistance(a: string, b: string): number {
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0]!;
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const kept = row[j]!;
      row[j] = Math.min(row[j]! + 1, row[j - 1]! + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = kept;
    }
  }
  return row[b.length]!;
}

/** One word written for another: the same, an initial of it, or a known short form. */
function shortFor(short: string, full: string): boolean {
  return short === full || (short.length === 1 && full.startsWith(short)) || (SHORT_FORMS[short] ?? []).includes(full);
}

/**
 * The likely reason two names are one person, read off the names alone (owner, 2026-09-27: "can be
 * auto generated?"). Only the cases the names themselves prove: initials and short forms ("Md Sheik"
 * for "Mohammed Sheik", "K. Gowda" for "Kaveri Gowda") and small spelling differences ("Laxmi",
 * "Lakshmi"). Anything that needs another document - a parent's name, a co-sharer - is left for
 * the officer. A suggestion, never a decision: the field stays editable.
 */
export function suggestNameMatchBasis(recorded?: string | null, reference?: string | null): string | undefined {
  const a = tokens(recorded ?? '');
  const b = tokens(reference ?? '');
  if (a.length === 0 || b.length === 0) return undefined;
  // The same words once a title (Smt., Sri, Late ...) is set aside: a short form of the name.
  if (a.join(' ') === b.join(' ')) return namesDiffer(recorded, reference) ? 'initials_short_form' : undefined;
  if (a.length === b.length) {
    const pairs = a.map((token, index) => [token, b[index]!] as const);
    if (pairs.every(([x, y]) => shortFor(x, y) || shortFor(y, x)) && pairs.some(([x, y]) => x !== y)) return 'initials_short_form';
    const alike = (x: string, y: string) => x === y || (x.length > 2 && y.length > 2
      && (sound(x) === sound(y) || editDistance(sound(x), sound(y)) <= Math.max(1, Math.floor(Math.max(x.length, y.length) / 4))));
    if (pairs.every(([x, y]) => alike(x, y))) {
      return 'spelling_variation';
    }
  }
  // A name that lost or gained a part: each word of the shorter stands for a word of the longer, in order.
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  let at = 0;
  for (const token of shorter) {
    while (at < longer.length && !shortFor(token, longer[at]!)) at += 1;
    if (at === longer.length) return undefined;
    at += 1;
  }
  return 'initials_short_form';
}
